import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { calculators } from "../data/tools";
import { ToolShell, Field, inputCls, ResultBox } from "../components/ToolShell";
import { AdSlot } from "../components/AdSlot";
import { usePageMeta } from "../lib/usePageMeta";
import { trackUsage } from "../lib/api";
import NotFound from "./NotFound";

const fmt = (n, d = 2) =>
  Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: d }) : "—";

function BMICalc() {
  const [unit, setUnit] = useState("metric");
  const [h, setH] = useState("");
  const [w, setW] = useState("");
  const { bmi, cat } = useMemo(() => {
    let bmi;
    if (unit === "metric") {
      const m = parseFloat(h) / 100;
      bmi = parseFloat(w) / (m * m);
    } else {
      bmi = (parseFloat(w) / (parseFloat(h) * parseFloat(h))) * 703;
    }
    let cat = "—";
    if (Number.isFinite(bmi)) {
      cat = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Normal weight" : bmi < 30 ? "Overweight" : "Obese";
    }
    return { bmi, cat };
  }, [unit, h, w]);
  return (
    <div className="space-y-6">
      <div className="flex gap-0 border-2 border-ink w-max">
        {["metric", "imperial"].map((u) => (
          <button key={u} data-testid={`bmi-unit-${u}`} onClick={() => setUnit(u)}
            className={`font-mono text-xs uppercase tracking-widest px-5 py-2.5 ${unit === u ? "bg-ink text-paper" : "bg-paper"}`}>
            {u}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={unit === "metric" ? "Height (cm)" : "Height (in)"}>
          <input data-testid="bmi-height" className={inputCls} type="number" value={h} onChange={(e) => setH(e.target.value)} placeholder="0" />
        </Field>
        <Field label={unit === "metric" ? "Weight (kg)" : "Weight (lb)"}>
          <input data-testid="bmi-weight" className={inputCls} type="number" value={w} onChange={(e) => setW(e.target.value)} placeholder="0" />
        </Field>
      </div>
      <ResultBox testid="bmi-result" label="Your BMI" value={fmt(bmi, 1)} sub={cat !== "—" ? `Category: ${cat}` : "Enter height & weight"} />
    </div>
  );
}

function PercentageCalc() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const res = useMemo(() => (parseFloat(a) / 100) * parseFloat(b), [a, b]);
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Percentage (%)">
          <input data-testid="pct-a" className={inputCls} type="number" value={a} onChange={(e) => setA(e.target.value)} placeholder="e.g. 15" />
        </Field>
        <Field label="Of value">
          <input data-testid="pct-b" className={inputCls} type="number" value={b} onChange={(e) => setB(e.target.value)} placeholder="e.g. 200" />
        </Field>
      </div>
      <ResultBox testid="pct-result" label={`${a || 0}% of ${b || 0}`} value={fmt(res, 4)} />
    </div>
  );
}

function LoanCalc() {
  const [p, setP] = useState("");
  const [r, setR] = useState("");
  const [y, setY] = useState("");
  const { emi, total, interest } = useMemo(() => {
    const P = parseFloat(p);
    const n = parseFloat(y) * 12;
    const i = parseFloat(r) / 100 / 12;
    let emi;
    if (i === 0) emi = P / n;
    else emi = (P * i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
    const total = emi * n;
    return { emi, total, interest: total - P };
  }, [p, r, y]);
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-5">
        <Field label="Amount"><input data-testid="loan-principal" className={inputCls} type="number" value={p} onChange={(e) => setP(e.target.value)} placeholder="100000" /></Field>
        <Field label="Rate (% / yr)"><input data-testid="loan-rate" className={inputCls} type="number" value={r} onChange={(e) => setR(e.target.value)} placeholder="7.5" /></Field>
        <Field label="Term (years)"><input data-testid="loan-years" className={inputCls} type="number" value={y} onChange={(e) => setY(e.target.value)} placeholder="20" /></Field>
      </div>
      <ResultBox testid="loan-result" label="Monthly Payment" value={fmt(emi)} sub={Number.isFinite(total) ? `Total paid: ${fmt(total)} · Interest: ${fmt(interest)}` : "Enter loan details"} />
    </div>
  );
}

function AgeCalc() {
  const [dob, setDob] = useState("");
  const out = useMemo(() => {
    if (!dob) return null;
    const b = new Date(dob);
    const now = new Date();
    if (b > now) return null;
    let y = now.getFullYear() - b.getFullYear();
    let m = now.getMonth() - b.getMonth();
    let d = now.getDate() - b.getDate();
    if (d < 0) { m--; d += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
    if (m < 0) { y--; m += 12; }
    const days = Math.floor((now - b) / 86400000);
    return { y, m, d, days };
  }, [dob]);
  return (
    <div className="space-y-6">
      <Field label="Date of birth">
        <input data-testid="age-dob" className={inputCls} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
      </Field>
      <ResultBox testid="age-result" label="Your age" value={out ? `${out.y}y ${out.m}m ${out.d}d` : "—"} sub={out ? `That's ${out.days.toLocaleString()} days alive.` : "Pick your date of birth"} />
    </div>
  );
}

const MAP = { bmi: BMICalc, percentage: PercentageCalc, loan: LoanCalc, age: AgeCalc };

export default function CalculatorPage() {
  const { slug } = useParams();
  const meta = calculators.find((c) => c.slug === slug);
  const Comp = MAP[slug];
  usePageMeta(meta ? `${meta.title} — Free Online | Utiliverse` : "Calculator", meta?.blurb || "");
  useEffect(() => {
    if (meta) trackUsage({ slug: `calc-${slug}`, title: meta.title, path: `/calculators/${slug}`, category: "calculator" });
  }, [meta, slug]);
  if (!meta || !Comp) return <NotFound />;
  return (
    <ToolShell crumb="Calculators" crumbTo="/calculators" title={meta.title} subtitle={meta.blurb} testid={`calculator-page-${slug}`}>
      <Comp />
      <div className="mt-12"><AdSlot format="leaderboard" testid="calculator-ad" /></div>
    </ToolShell>
  );
}
